import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Breadcrumb from '../Breadcrumb';
import DateTimeRangePicker from '../DateTimeRangePicker';

interface UsageProps {
  projectId: string;
}

const Usage: React.FC<UsageProps> = ({ projectId }) => {
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    date.setHours(0, 0, 0, 0);
    return date;
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  });
  const [usageData, setUsageData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'tokens' | 'cost'>('tokens');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchUsageData();
  }, [projectId, startDate, endDate]);

  const getCacheKey = () => `usage_${projectId}`;

  const getCachedData = () => {
    try {
      const cached = localStorage.getItem(getCacheKey());
      return cached ? JSON.parse(cached) : { ranges: [], data: [] };
    } catch {
      return { ranges: [], data: [] };
    }
  };

  const setCachedData = (cache: any) => {
    try {
      localStorage.setItem(getCacheKey(), JSON.stringify(cache));
    } catch {}
  };

  const fetchUsageData = async () => {
    const requestStart = startDate.getTime();
    const requestEnd = endDate.getTime();
    const cache = getCachedData();
    
    const overlaps = cache.ranges.filter((r: any) => 
      r.start <= requestEnd && r.end >= requestStart
    );
    
    let gaps = [{ start: requestStart, end: requestEnd }];
    overlaps.forEach((overlap: any) => {
      gaps = gaps.flatMap(gap => {
        if (gap.end < overlap.start || gap.start > overlap.end) return [gap];
        const parts = [];
        if (gap.start < overlap.start) parts.push({ start: gap.start, end: overlap.start - 1 });
        if (gap.end > overlap.end) parts.push({ start: overlap.end + 1, end: gap.end });
        return parts;
      });
    });

    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      for (const gap of gaps) {
        const params = new URLSearchParams({
          start_time: new Date(gap.start).toISOString(),
          end_time: new Date(gap.end).toISOString()
        });
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/usage?${params}`, {
          headers: {
            'Authorization': token || ''
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          cache.data.push({ ...data, timestamp: gap.start });
          cache.ranges.push({ start: gap.start, end: gap.end });
        }
      }
      
      cache.ranges.sort((a: any, b: any) => a.start - b.start);
      const merged = [];
      for (const range of cache.ranges) {
        if (merged.length && merged[merged.length - 1].end >= range.start - 1) {
          merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, range.end);
        } else {
          merged.push(range);
        }
      }
      cache.ranges = merged;
      
      setCachedData(cache);
      
      const relevantData = cache.data.filter((d: any) => 
        d.timestamp >= requestStart && d.timestamp <= requestEnd
      );
      
      if (relevantData.length > 0) {
        const mergedUsage: any = {};
        relevantData.forEach((d: any) => {
          Object.entries(d.usage || {}).forEach(([model, runTypes]: [string, any]) => {
            if (!mergedUsage[model]) mergedUsage[model] = {};
            Object.entries(runTypes).forEach(([runType, data]: [string, any]) => {
              if (!mergedUsage[model][runType]) {
                mergedUsage[model][runType] = { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0, prompt_cost: 0, completion_cost: 0, run_count: 0 };
              }
              const existing = mergedUsage[model][runType];
              existing.prompt_tokens += data.prompt_tokens || 0;
              existing.completion_tokens += data.completion_tokens || 0;
              existing.total_tokens += data.total_tokens || 0;
              existing.prompt_cost += data.prompt_cost || 0;
              existing.completion_cost += data.completion_cost || 0;
              existing.run_count += data.run_count || 0;
            });
          });
        });
        setUsageData({ usage: mergedUsage });
      }
      
    } catch (error) {
      console.error('Error fetching usage data:', error);
    }
    setLoading(false);
  };



  const prepareRunTypeData = () => {
    if (!usageData?.usage) return [];
    
    const runTypeData: { [key: string]: any } = {};
    
    Object.values(usageData.usage).forEach((modelData: any) => {
      Object.entries(modelData).forEach(([runType, data]: [string, any]) => {
        if (!runTypeData[runType]) {
          runTypeData[runType] = { 
            prompt_tokens: 0, completion_tokens: 0, total_tokens: 0,
            prompt_cost: 0, completion_cost: 0, total_cost: 0
          };
        }
        runTypeData[runType].prompt_tokens += data.prompt_tokens;
        runTypeData[runType].completion_tokens += data.completion_tokens;
        runTypeData[runType].total_tokens += data.total_tokens;
        runTypeData[runType].prompt_cost += data.prompt_cost || 0;
        runTypeData[runType].completion_cost += data.completion_cost || 0;
        runTypeData[runType].total_cost += (data.prompt_cost || 0) + (data.completion_cost || 0);
      });
    });
    
    return Object.entries(runTypeData).map(([runType, data]) => ({
      name: runType,
      prompt_tokens: data.prompt_tokens,
      completion_tokens: data.completion_tokens,
      total_tokens: data.total_tokens,
      prompt_cost: data.prompt_cost,
      completion_cost: data.completion_cost,
      total_cost: data.total_cost
    }));
  };

  const prepareModelData = () => {
    if (!usageData?.usage) return [];
    
    return Object.entries(usageData.usage).map(([modelName, runTypes]: [string, any]) => {
      const totals = Object.values(runTypes).reduce((acc: any, data: any) => ({
        prompt_tokens: acc.prompt_tokens + data.prompt_tokens,
        completion_tokens: acc.completion_tokens + data.completion_tokens,
        total_tokens: acc.total_tokens + data.total_tokens,
        prompt_cost: acc.prompt_cost + (data.prompt_cost || 0),
        completion_cost: acc.completion_cost + (data.completion_cost || 0),
        total_cost: acc.total_cost + (data.prompt_cost || 0) + (data.completion_cost || 0)
      }), { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0, prompt_cost: 0, completion_cost: 0, total_cost: 0 }) as any;
      
      return {
        name: modelName,
        prompt_tokens: totals.prompt_tokens,
        completion_tokens: totals.completion_tokens,
        total_tokens: totals.total_tokens,
        prompt_cost: totals.prompt_cost,
        completion_cost: totals.completion_cost,
        total_cost: totals.total_cost
      };
    });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedTableData = () => {
    const tableData: Array<{modelName: string, runType: string, data: any}> = [];
    
    Object.entries(usageData?.usage || {}).forEach(([modelName, runTypes]: [string, any]) => {
      Object.entries(runTypes).forEach(([runType, data]: [string, any]) => {
        tableData.push({ modelName, runType, data });
      });
    });
    
    if (!sortField) return tableData;
    
    return tableData.sort((a, b) => {
      let aVal, bVal;
      
      if (sortField === 'model') {
        aVal = a.modelName;
        bVal = b.modelName;
      } else if (sortField === 'runType') {
        aVal = a.runType;
        bVal = b.runType;
      } else if (sortField === 'total_cost') {
        aVal = (a.data.prompt_cost || 0) + (a.data.completion_cost || 0);
        bVal = (b.data.prompt_cost || 0) + (b.data.completion_cost || 0);
      } else {
        aVal = a.data[sortField] || 0;
        bVal = b.data[sortField] || 0;
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId, path: `/project/${projectId}` },
        { label: 'Usage' }
      ]} />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Usage Dashboard</h2>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div>
            <label style={{ marginRight: '0.5rem', fontWeight: 'bold' }}>View:</label>
            <select 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value as 'tokens' | 'cost')}
              style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="tokens">Tokens</option>
              <option value="cost">Cost ($)</option>
            </select>
          </div>
          <DateTimeRangePicker
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
          <button 
            onClick={() => {
              localStorage.removeItem(getCacheKey());
              fetchUsageData();
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          Loading usage data...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem', height: '500px', overflowY: 'auto' }}>
          <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>Usage by Run Type</h4>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={prepareRunTypeData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} />
                <YAxis />
                <Tooltip />
                {viewMode === 'tokens' ? (
                  <>
                    <Bar dataKey="prompt_tokens" stackId="a" fill="#8884d8" name="Prompt Tokens" />
                    <Bar dataKey="completion_tokens" stackId="a" fill="#82ca9d" name="Completion Tokens" />
                  </>
                ) : (
                  <>
                    <Bar dataKey="prompt_cost" stackId="a" fill="#8884d8" name="Prompt Cost" />
                    <Bar dataKey="completion_cost" stackId="a" fill="#82ca9d" name="Completion Cost" />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
            <h4 style={{ margin: '0 0 1rem 0' }}>Usage by Model</h4>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={prepareModelData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} />
                <YAxis />
                <Tooltip />
                {viewMode === 'tokens' ? (
                  <>
                    <Bar dataKey="prompt_tokens" stackId="a" fill="#8884d8" name="Prompt Tokens" />
                    <Bar dataKey="completion_tokens" stackId="a" fill="#82ca9d" name="Completion Tokens" />
                  </>
                ) : (
                  <>
                    <Bar dataKey="prompt_cost" stackId="a" fill="#8884d8" name="Prompt Cost" />
                    <Bar dataKey="completion_cost" stackId="a" fill="#82ca9d" name="Completion Cost" />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {usageData && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '1rem' }}>
          <h4>Usage Summary</h4>
          <p><strong>Period:</strong> {startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}</p>
          <p><strong>Total Cost:</strong> ${Object.values(usageData.usage || {}).reduce((total: number, modelData: any) => 
            total + Object.values(modelData).reduce((modelTotal: number, data: any) => 
              modelTotal + (data.prompt_cost || 0) + (data.completion_cost || 0), 0), 0).toFixed(6)}</p>
          
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('model')}>Model {sortField === 'model' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('runType')}>Run Type {sortField === 'runType' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('prompt_tokens')}>Prompt Tokens {sortField === 'prompt_tokens' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('completion_tokens')}>Completion Tokens {sortField === 'completion_tokens' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('total_tokens')}>Total Tokens {sortField === 'total_tokens' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('prompt_cost')}>Prompt Cost ($) {sortField === 'prompt_cost' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('completion_cost')}>Completion Cost ($) {sortField === 'completion_cost' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('total_cost')}>Total Cost ($) {sortField === 'total_cost' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
                <th style={{ padding: '0.75rem', textAlign: 'right', borderBottom: '1px solid #ddd', cursor: 'pointer' }} onClick={() => handleSort('run_count')}>Runs {sortField === 'run_count' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              </tr>
            </thead>
            <tbody>
              {getSortedTableData().map(({ modelName, runType, data }, index) => (
                  <tr key={`${modelName}-${runType}`} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>{modelName}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>{runType}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>{data.prompt_tokens.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>{data.completion_tokens.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>{data.total_tokens.toLocaleString()}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>${(data.prompt_cost || 0).toFixed(6)}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>${(data.completion_cost || 0).toFixed(6)}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>${((data.prompt_cost || 0) + (data.completion_cost || 0)).toFixed(6)}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'right' }}>{data.run_count}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Usage;